import { InsightsApi, PlatformEnum } from '@insights/insights-data-provider';
import { getCountry, getLanguage } from 'i18n/i18nUtils';
import { getExperiment } from 'optimizely/utils';
import atMostOnce from 'utils/object-state/atMostOnce';
import waitFor from 'utils/object-state/waitFor';
import { isKiosk, isProduction } from 'utils/platform';
import AnalyticsEvent from '../AnalyticsEvent';
import AnalyticsService from '../AnalyticsService';
import standardInsightsEvents from './standardInsightsEvents';
import packageInfo from '../../../package.json';

/**
 * Reports events to IPEX Insights via {@link InsightsApi}.
 */
export default class InsightsService implements AnalyticsService {
  private didConnect: boolean = false;

  constructor(
    private readonly insightsApi: InsightsApi,
    private readonly locale: string,
    private readonly applicationName: string,
    private readonly storeId: string | null,
    private readonly kioskId: string | null
  ) {}

  @atMostOnce
  async connect() {
    const countryCode = getCountry(this.locale).toLowerCase(); // needs to be lower case or else INGKAs google analytics script can't map country code to UA-tracking id
    const languageCode = getLanguage(this.locale).toLowerCase();
    const devMode = !isProduction();

    const experiment = await getExperiment();

    try {
      await this.insightsApi.connectApplication({
        applicationId: this.applicationName,
        applicationVersion: packageInfo.version,
        platform: PlatformEnum.owfe,
        countryCode,
        languageCode,
        kiosk: isKiosk(),
        storeId: (isKiosk() && this.storeId) || undefined,
        kioskId: (isKiosk() && this.kioskId) || undefined,
        dev: devMode,
        isPlanner: true,
        enableGa: true,
        abVersionName: experiment?.name,
        abVersionVariation: experiment?.variation
      });
      /* eslint-disable  no-console */
      console.log('IPEX insights initialized');
      console.log(`Analytics dev mode: ${devMode}`);
      console.log(`Planner version: ${packageInfo.version}`);
      console.log(`Store Id: ${this.storeId}`);

      this.didConnect = true;
    } catch (e) {
      console.error('Failed to initialize IPEX insights', e);
      /* eslint-enable  no-console */
    }
  }

  @waitFor('connect')
  report(event: AnalyticsEvent): void {
    if (!this.didConnect) {
      return;
    }

    try {
      Object.values(standardInsightsEvents).some(spec =>
        spec.sendToInsights(this.insightsApi, event)
      ) ||
        this.insightsApi.sendEvent({
          event: event.eventName,
          ipexMoment: event.ipexMoment,
          payload: event.payload
        });
    } catch (error) {
      //Failed to fetch
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }
}
